import { module as BaseModule } from "modujs";
import { createElement } from "react";
import { createRoot } from "react-dom/client";
import ProjectStoriesOverlay from "../components/ProjectStoriesOverlay.jsx";
import Slideshow from "./Slideshow.js";
import YoutubeVideo from "./YoutubeVideo.js";

const SELECTORS = {
  item: "[data-project-stories-item]",
  trigger: "[data-project-stories-trigger]",
  title: ".b-project-stories__item-title",
  location: ".b-project-stories__item-text > span",
  details: ".b-project-stories__item-details",
  slideshow: "[data-module-slideshow]",
  caption: ".b-project-stories__item-image-caption",
  bullets: ".b-project-stories__item-bullets",
};

export default class ProjectStories extends BaseModule {
  init() {
    this.items = this.collectItems();

    if (!this.items.length) {
      return;
    }

    this.mountTarget = document.createElement("div");
    this.mountTarget.className = "b-project-stories__overlay-root";
    this.el.appendChild(this.mountTarget);

    this.isOpen = false;
    this.activeItem = null;
    this.overlaySlideshow = null;
    this.overlayYoutubeModules = [];
    this.overlayContainer = null;

    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleOverlayContent = this.handleOverlayContent.bind(this);
    this.handleOverlayContent = this.handleOverlayContent.bind(this);

    this.attachTriggerListeners();
    document.addEventListener("keydown", this.handleKeyDown);
    this.render();
  }

  collectItems() {
    const nodes = Array.from(this.el.querySelectorAll(SELECTORS.item));

    return nodes
      .map((node, index) => {
        const trigger = node.querySelector(SELECTORS.trigger);

        if (!trigger) {
          return null;
        }

        return {
          id: node.id || `project-story-${index}`,
          trigger,
          titleEl: node.querySelector(SELECTORS.title),
          locationEl: node.querySelector(SELECTORS.location),
          detailsEl: node.querySelector(SELECTORS.details),
          slideshowEl: node.querySelector(SELECTORS.slideshow),
          slug: node.getAttribute("data-project-stories-slug") || "",
          element: node,
        };
      })
      .filter(Boolean);
  }

  attachTriggerListeners() {
    this.items.forEach((item) => {
      const handler = (event) => this.handleTriggerClick(event, item);
      item.trigger.addEventListener("click", handler);
      item.triggerHandler = handler;
    });
  }

  detachTriggerListeners() {
    this.items.forEach((item) => {
      if (item.trigger && item.triggerHandler) {
        item.trigger.removeEventListener("click", item.triggerHandler);
        item.triggerHandler = null;
      }
    });
  }

  handleTriggerClick(event, item) {
    event.preventDefault();

    this.openItem(item);
  }

  openItem(item) {
    const serialized = this.serializeItem(item);

    if (!serialized) {
      return;
    }

    this.overlayContainer = null;
    this.activeItem = serialized;
    this.isOpen = true;
    this.updateHash(serialized.slug);
    this.render();
  }

  serializeItem(item) {
    if (!item) {
      return null;
    }

    return {
      id: item.id,
      title: item.titleEl ? item.titleEl.textContent.trim() : "",
      location: item.locationEl ? item.locationEl.textContent.trim() : "",
      detailsHTML: item.detailsEl ? item.detailsEl.innerHTML.trim() : "",
      slideshowHTML: this.getSlideshowHTML(item.slideshowEl),
      slug: item.slug || "",
    };
  }

  getSlideshowHTML(slideshowEl) {
    if (!slideshowEl) {
      return "";
    }

    const clone = slideshowEl.cloneNode(true);

    clone.style.height = "";
    clone.setAttribute("data-module-slideshow", "");

    clone.querySelectorAll("[hidden]").forEach((node) => {
      node.removeAttribute("hidden");
    });

    return clone.outerHTML;
  }

  handleClose() {
    if (!this.isOpen) {
      return;
    }

    this.isOpen = false;
    this.activeItem = null;
    this.clearHash();
    this.render();
  }

  handleKeyDown(event) {
    if (event.key === "Escape" && this.isOpen) {
      this.handleClose();
    }
  }

  handleOverlayContent(container) {
    if (container === this.overlayContainer) {
      return;
    }

    if (!container) {
      this.overlayContainer = null;
      this.destroyOverlaySlideshow();
      this.destroyOverlayYoutubeModules();
      return;
    }

    this.overlayContainer = container;

    this.initOverlaySlideshow();
    this.initOverlayYoutubeModules();
  }

  initOverlaySlideshow() {
    this.destroyOverlaySlideshow();

    if (!this.isOpen || !this.overlayContainer) {
      return;
    }

    const slideshowEl =
      this.overlayContainer.querySelector("[data-module-slideshow]") ||
      this.overlayContainer.closest("[data-module-slideshow]");

    if (!slideshowEl) {
      return;
    }

    const instance = new Slideshow({
      el: slideshowEl,
      name: "slideshow",
      dataName: "slideshow",
    });

    instance.mInit(this.modules);
    instance.init();

    this.overlaySlideshow = instance;
  }

  destroyOverlaySlideshow() {
    if (!this.overlaySlideshow) {
      return;
    }

    this.overlaySlideshow.destroy();
    this.overlaySlideshow = null;
  }

  initOverlayYoutubeModules() {
    this.destroyOverlayYoutubeModules();

    if (!this.overlayContainer) {
      return;
    }

    const moduleEls = Array.from(
      this.overlayContainer.querySelectorAll("[data-module-youtube-video]")
    );

    this.overlayYoutubeModules = moduleEls.map((el) => {
      const instance = new YoutubeVideo({
        el,
        name: "youtubeVideo",
        dataName: "youtubeVideo",
      });

      instance.mInit(this.modules);
      instance.init();

      return instance;
    });
  }

  destroyOverlayYoutubeModules() {
    if (!this.overlayYoutubeModules.length) {
      return;
    }

    this.overlayYoutubeModules.forEach((instance) => {
      if (instance && typeof instance.destroy === "function") {
        instance.destroy();
      }
    });

    this.overlayYoutubeModules = [];
  }

  render() {
    if (!this.mountTarget) {
      return;
    }

    if (!this.reactRoot) {
      this.reactRoot = createRoot(this.mountTarget);
    }

    this.reactRoot.render(
      createElement(ProjectStoriesOverlay, {
        isOpen: this.isOpen,
        story: this.activeItem,
        onClose: this.handleClose,
        onContentReady: this.handleOverlayContent,
      })
    );

    if (!this.isOpen) {
      this.openFromHash();
    }
  }

  updateHash(slug) {
    if (!slug) {
      this.clearHash();
      return;
    }

    const { pathname, search } = window.location;
    const nextUrl = `${pathname}${search}#${slug}`;
    window.history.replaceState(null, "", nextUrl);
  }

  clearHash() {
    const { pathname, search, hash } = window.location;

    if (!hash) {
      return;
    }

    window.history.replaceState(null, "", `${pathname}${search}`);
  }

  openFromHash() {
    const slug = this.getSlugFromHash();

    if (!slug) {
      return;
    }

    const item = this.items.find(
      (entry) => entry.slug && entry.slug.toLowerCase() === slug.toLowerCase()
    );

    if (item) {
      this.scrollBlockIntoView(item);
      this.openItem(item);
    }
  }

  scrollBlockIntoView(item) {
    if (item?.element) {
      item.element.scrollIntoView({ behavior: "auto", block: "start" });
      return;
    }

    if (this.el && this.el.scrollIntoView) {
      this.el.scrollIntoView({ behavior: "auto", block: "start" });
    }
  }

  getSlugFromHash() {
    const { hash } = window.location;

    if (!hash || hash === "#") {
      return "";
    }

    return decodeURIComponent(hash.slice(1));
  }

  destroy() {
    this.detachTriggerListeners();
    this.destroyOverlaySlideshow();
    this.destroyOverlayYoutubeModules();
    this.overlayContainer = null;
    document.removeEventListener("keydown", this.handleKeyDown);

    if (this.reactRoot) {
      this.reactRoot.unmount();
      this.reactRoot = null;
    }

    if (this.mountTarget) {
      this.mountTarget.remove();
      this.mountTarget = null;
    }

    this.items = [];
    this.activeItem = null;
    this.isOpen = false;
  }
}
